import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import AccessControl from '../../components/AccessControl';
import NewReportModal from './NewReportModal';
import './SyntheticAudienceWorkspace.css';
import config from '../../config';
import folderIcon from '../../public/img/folder_icon.svg';

const SyntheticAudienceWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [personas, setPersonas] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [reports, setReports] = useState([]);
  const [totalCounts, setTotalCounts] = useState({
    personas: 0,
    surveys: 0,
    reports: 0
  });
  const [folders, setFolders] = useState({
    personas: [],
    surveys: [],
    reports: []
  });
  const [loading, setLoading] = useState({
    personas: true,
    surveys: true,
    reports: true
  });
  const [error, setError] = useState(null);
  const [newReportModalOpen, setNewReportModalOpen] = useState(false);
  const [currentFolders, setCurrentFolders] = useState({
    personas: [],
    surveys: [],
    reports: []
  });
  const [newFolderModal, setNewFolderModal] = useState({
    open: false,
    section: null,
    name: '',
    access: ['All']
  });
  const [folderAccessModal, setFolderAccessModal] = useState({
    open: false,
    folderId: null,
    folderName: '',
    access: ['All']
  });
  const [reportAccessModal, setReportAccessModal] = useState({
    open: false,
    reportId: null,
    reportTitle: '',
    access: ['All']
  });

  const resetNewFolderModal = () => {
    setNewFolderModal({
      open: false,
      section: null,
      name: '',
      access: ['All']
    });
  };

  const openFolderAccessModal = (folder) => {
    setFolderAccessModal({
      open: true,
      folderId: folder._id,
      folderName: folder.name,
      access: folder.access && folder.access.length > 0 ? folder.access : ['All']
    });
  };

  const closeFolderAccessModal = () => {
    setFolderAccessModal({
      open: false,
      folderId: null,
      folderName: '',
      access: ['All']
    });
  };

  const openReportAccessModal = (report) => {
    setReportAccessModal({
      open: true,
      reportId: report._id,
      reportTitle: report.title || 'Untitled Report',
      access: report.access && report.access.length > 0 ? report.access : ['All']
    });
  };

  const closeReportAccessModal = () => {
    setReportAccessModal({
      open: false,
      reportId: null,
      reportTitle: '',
      access: ['All']
    });
  };

  // Fetch total counts on mount (once)
  useEffect(() => {
    fetchTotalCounts();
  }, []);

  // Parse URL params on mount and when location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const personasPath = params.get('personas_folder')?.split(',').filter(Boolean) || [];
    const surveysPath = params.get('surveys_folder')?.split(',').filter(Boolean) || [];
    const reportsPath = params.get('reports_folder')?.split(',').filter(Boolean) || [];

    setCurrentFolders({
      personas: personasPath,
      surveys: surveysPath,
      reports: reportsPath
    });
  }, [location.search]);

  // Fetch data when folders change
  useEffect(() => {
    fetchSection('personas');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolders.personas.join(',')]);

  useEffect(() => {
    fetchSection('surveys');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolders.surveys.join(',')]);

  useEffect(() => {
    fetchSection('reports');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolders.reports.join(',')]);

  const fetchTotalCounts = async () => {
    try {
      // Fetch counts without folder filtering - get all items recursively
      const [personasRes, surveysRes, reportsRes] = await Promise.all([
        fetch(`${config.api.baseUrl}/synthetic-audience/personas`, {
          credentials: 'include'
        }),
        fetch(`${config.api.baseUrl}/synthetic-audience/surveys`, {
          credentials: 'include'
        }),
        fetch(`${config.api.baseUrl}/synthetic-audience/reports`, {
          credentials: 'include'
        })
      ]);

      if (personasRes.ok && surveysRes.ok && reportsRes.ok) {
        const personasData = await personasRes.json();
        const surveysData = await surveysRes.json();
        const reportsData = await reportsRes.json();

        setTotalCounts({
          personas: (personasData.personas || []).length,
          surveys: (surveysData.surveys || []).length,
          reports: (reportsData.reports || []).length
        });
      }
    } catch (err) {
      console.error('Error fetching total counts:', err);
    }
  };

  const fetchSection = async (section) => {
    setLoading(prev => ({ ...prev, [section]: true }));
    setError(null);

    try {
      const folderPath = currentFolders[section];
      const itemsQuery = folderPath.length > 0
        ? `?folder_path=${folderPath.join(',')}`
        : '?folder_path=';
      const foldersQuery = folderPath.length > 0
        ? `?section=${section}&folder_path=${folderPath.join(',')}`
        : `?section=${section}&folder_path=`;

      const endpoint = section === 'personas' ? 'personas' : section === 'surveys' ? 'surveys' : 'reports';

      const [itemsRes, foldersRes] = await Promise.all([
        fetch(`${config.api.baseUrl}/synthetic-audience/${endpoint}${itemsQuery}`, {
          credentials: 'include'
        }),
        fetch(`${config.api.baseUrl}/synthetic-audience/folders${foldersQuery}`, {
          credentials: 'include'
        })
      ]);

      if (!itemsRes.ok || !foldersRes.ok) {
        throw new Error(`Failed to fetch ${section}`);
      }

      const itemsData = await itemsRes.json();
      const foldersData = await foldersRes.json();

      if (section === 'personas') {
        setPersonas(itemsData.personas || []);
      } else if (section === 'surveys') {
        setSurveys(itemsData.surveys || []);
      } else if (section === 'reports') {
        setReports(itemsData.reports || []);
      }

      setFolders(prev => ({
        ...prev,
        [section]: foldersData.folders || []
      }));
    } catch (err) {
      console.error(`Error fetching ${section}:`, err);
      setError(`Failed to load ${section}. Please try again.`);
    } finally {
      setLoading(prev => ({ ...prev, [section]: false }));
    }
  };

  const fetchData = async () => {
    await Promise.all([
      fetchSection('personas'),
      fetchSection('surveys'),
      fetchSection('reports')
    ]);
  };

  const handleDeletePersona = async (personaId) => {
    if (!window.confirm('Are you sure you want to delete this persona? This will also delete all associated chats.')) {
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/personas/${personaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setPersonas(personas.filter(p => p._id !== personaId));
        fetchTotalCounts(); // Update total counts
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete persona');
      }
    } catch (err) {
      console.error('Error deleting persona:', err);
      alert('Failed to delete persona');
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey? This will also delete all responses.')) {
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/surveys/${surveyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setSurveys(surveys.filter(s => s._id !== surveyId));
        fetchTotalCounts(); // Update total counts
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete survey');
      }
    } catch (err) {
      console.error('Error deleting survey:', err);
      alert('Failed to delete survey');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report? This will also delete all responses.')) {
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/reports/${reportId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setReports(reports.filter(r => r._id !== reportId));
        fetchTotalCounts(); // Update total counts
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete report');
      }
    } catch (err) {
      console.error('Error deleting report:', err);
      alert('Failed to delete report');
    }
  };

  const handleReportCreated = (reportId) => {
    // Refresh data to show new report
    fetchData();
    fetchTotalCounts(); // Update total counts
    // Navigate to report results page
    navigate(`/synthetic-audience-workshop/reports/${reportId}/results`);
  };

  // Folder navigation functions
  const navigateToFolder = (section, path) => {
    const params = new URLSearchParams(location.search);
    if (path.length > 0) {
      params.set(`${section}_folder`, path.join(','));
    } else {
      params.delete(`${section}_folder`);
    }
    navigate(`?${params.toString()}`);
  };

  const arraysEqual = (a, b) => {
    return a.length === b.length && a.every((val, idx) => val === b[idx]);
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder?')) {
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/folders/${folderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Failed to delete folder');
        return;
      }

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error('Error deleting folder:', err);
      alert('Failed to delete folder');
    }
  };

  const handleCreateFolder = async (section, folderName, folderAccess = ['All']) => {
    // Validation
    const trimmedName = folderName?.trim();
    if (!trimmedName) {
      alert('Please enter a folder name');
      return;
    }
    if (trimmedName.length > 50) {
      alert('Folder name must be 50 characters or less');
      return;
    }
    if (trimmedName.includes('/')) {
      alert('Folder name cannot contain slashes');
      return;
    }
    if (!Array.isArray(folderAccess) || folderAccess.length === 0) {
      alert('Please select at least one access option');
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: trimmedName,
          section: section,
          folder_path: currentFolders[section],
          access: folderAccess
        })
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Failed to create folder');
        return;
      }

      // Close modal and refresh data
      resetNewFolderModal();
      await fetchData();
    } catch (err) {
      console.error('Error creating folder:', err);
      alert('Failed to create folder');
    }
  };

  const handleUpdateFolderAccess = async () => {
    if (!folderAccessModal.folderId) return;
    if (!Array.isArray(folderAccessModal.access) || folderAccessModal.access.length === 0) {
      alert('Please select at least one access option');
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/folders/${folderAccessModal.folderId}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ access: folderAccessModal.access })
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Failed to update folder access');
        return;
      }

      closeFolderAccessModal();
      await fetchData();
    } catch (err) {
      console.error('Error updating folder access:', err);
      alert('Failed to update folder access');
    }
  };

  const handleUpdateReportAccess = async () => {
    if (!reportAccessModal.reportId) return;
    if (!Array.isArray(reportAccessModal.access) || reportAccessModal.access.length === 0) {
      alert('Please select at least one access option');
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/reports/${reportAccessModal.reportId}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ access: reportAccessModal.access })
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Failed to update report access');
        return;
      }

      closeReportAccessModal();
      await fetchData();
    } catch (err) {
      console.error('Error updating report access:', err);
      alert('Failed to update report access');
    }
  };

  // Sort by newest first (created_at or updated_at)
  const sortedPersonas = [...personas].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at);
    const dateB = new Date(b.updated_at || b.created_at);
    return dateB - dateA; // Newest first
  });

  const sortedSurveys = [...surveys].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at);
    const dateB = new Date(b.updated_at || b.created_at);
    return dateB - dateA; // Newest first
  });

  const sortedReports = [...reports].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA; // Newest first
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'running': return 'status-running';
      case 'draft': return 'status-draft';
      case 'failed': return 'status-failed';
      default: return 'status-draft';
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="workspace-error">
          <p>{error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="synthetic-audience-workspace">
      <header className="workspace-header">
        <h1>Synthetic Audience Workshop</h1>
        <div className="workspace-stats">
          <div className="stat-card">
            <span className="stat-number">{totalCounts.personas}</span>
            <span className="stat-label">Personas</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{totalCounts.surveys}</span>
            <span className="stat-label">Surveys</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{totalCounts.reports}</span>
            <span className="stat-label">Reports</span>
          </div>
        </div>
      </header>

      <div className="workspace-content">
        {/* Personas Section */}
        <div className="workspace-section">
          <div className="section-header">
            <div className="section-title-area">
              <h2>Personas</h2>
              {currentFolders.personas.length > 0 && (
                <div className="breadcrumb">
                  <button onClick={() => navigateToFolder('personas', [])} className="breadcrumb-link">
                    Root
                  </button>
                  {currentFolders.personas.map((segment, idx) => (
                    <React.Fragment key={idx}>
                      <span className="breadcrumb-separator">›</span>
                      <button
                        onClick={() => navigateToFolder('personas', currentFolders.personas.slice(0, idx + 1))}
                        className="breadcrumb-link"
                      >
                        {segment}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            <div className="section-header-buttons">
              <button
                className="btn-secondary"
                onClick={() => setNewFolderModal({ open: true, section: 'personas', name: '', access: ['All'] })}
              >
                + New Folder
              </button>
              <button
                className="btn-primary"
                onClick={() => navigate('/synthetic-audience-workshop/personas/new', {
                  state: { defaultFolderPath: currentFolders.personas }
                })}
              >
                + New Persona
              </button>
            </div>
          </div>

          <div className="cards-container">
            {/* Render folders first */}
            {loading.personas ? (
              <div className="section-loading">
                <div className="section-loading-spinner"></div>
                <div className="section-loading-text">Loading personas...</div>
              </div>
            ) : (
              <>
                {folders.personas.map(folder => (
                  <div
                    key={folder._id}
                    className="card folder-card"
                    onClick={() => navigateToFolder('personas', [...currentFolders.personas, folder.name])}
                  >
                    <div className="folder-card-content">
                      <img src={folderIcon} alt="Folder" className="folder-icon" />
                      <h3>{folder.name}</h3>
                    </div>
                    <button
                      className="folder-access-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFolderAccessModal(folder);
                      }}
                      title="Edit folder access"
                    >
                      👥
                    </button>
                    <button
                      className="folder-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder._id);
                      }}
                      title="Delete folder"
                    >
                      ×
                    </button>
                  </div>
                ))}

            {/* Then render items at current level */}
            {sortedPersonas.filter(p => arraysEqual(p.folder_path || [], currentFolders.personas)).length === 0 && folders.personas.length === 0 ? (
              <div className="empty-state">
                <p>No personas in this folder yet</p>
                <p className="empty-subtitle">Create your first synthetic persona to get started</p>
              </div>
            ) : (
              sortedPersonas.filter(p => arraysEqual(p.folder_path || [], currentFolders.personas)).map((persona) => (
                <div key={persona._id} className="card persona-card">
                  <div className="card-header">
                    <h3>{persona.name}</h3>
                    <div className="card-badges">
                      {persona.files && persona.files.length > 0 && (
                        <span className="badge badge-files">
                          {persona.files.length} {persona.files.length === 1 ? 'file' : 'files'}
                        </span>
                      )}
                      {persona.vector_store_id && (
                        <span className="badge badge-trained">Trained</span>
                      )}
                    </div>
                  </div>

                  {persona.description && (
                    <p className="card-description">{persona.description}</p>
                  )}

                  <div className="card-actions">
                    <button
                      className="btn-action btn-chat"
                      onClick={() => navigate(`/synthetic-audience-workshop/personas/${persona._id}/chat`)}
                    >
                      Chat
                    </button>
                    <button
                      className="btn-action btn-edit"
                      onClick={() => navigate(`/synthetic-audience-workshop/personas/${persona._id}`)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleDeletePersona(persona._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
              </>
            )}
          </div>
        </div>

        {/* Surveys Section */}
        <div className="workspace-section">
          <div className="section-header">
            <div className="section-title-area">
              <h2>Surveys</h2>
              {currentFolders.surveys.length > 0 && (
                <div className="breadcrumb">
                  <button onClick={() => navigateToFolder('surveys', [])} className="breadcrumb-link">
                    Root
                  </button>
                  {currentFolders.surveys.map((segment, idx) => (
                    <React.Fragment key={idx}>
                      <span className="breadcrumb-separator">›</span>
                      <button
                        onClick={() => navigateToFolder('surveys', currentFolders.surveys.slice(0, idx + 1))}
                        className="breadcrumb-link"
                      >
                        {segment}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            <div className="section-header-buttons">
              <button
                className="btn-secondary"
                onClick={() => setNewFolderModal({ open: true, section: 'surveys', name: '', access: ['All'] })}
              >
                + New Folder
              </button>
              <button
                className="btn-primary"
                onClick={() => navigate('/synthetic-audience-workshop/surveys/new', {
                  state: { defaultFolderPath: currentFolders.surveys }
                })}
              >
                + New Survey
              </button>
            </div>
          </div>

          <div className="cards-container">
            {loading.surveys ? (
              <div className="section-loading">
                <div className="section-loading-spinner"></div>
                <div className="section-loading-text">Loading surveys...</div>
              </div>
            ) : (
              <>
                {/* Render folders first */}
                {folders.surveys.map(folder => (
                  <div
                    key={folder._id}
                    className="card folder-card"
                    onClick={() => navigateToFolder('surveys', [...currentFolders.surveys, folder.name])}
                  >
                    <div className="folder-card-content">
                      <img src={folderIcon} alt="Folder" className="folder-icon" />
                      <h3>{folder.name}</h3>
                    </div>
                    <button
                      className="folder-access-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFolderAccessModal(folder);
                      }}
                      title="Edit folder access"
                    >
                      👥
                    </button>
                    <button
                      className="folder-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder._id);
                      }}
                      title="Delete folder"
                    >
                      ×
                    </button>
                  </div>
                ))}

                {/* Then render items at current level */}
                {sortedSurveys.filter(s => arraysEqual(s.folder_path || [], currentFolders.surveys)).length === 0 && folders.surveys.length === 0 ? (
              <div className="empty-state">
                <p>No surveys in this folder yet</p>
                <p className="empty-subtitle">Create a survey to gather insights from your personas</p>
              </div>
            ) : (
              sortedSurveys.filter(s => arraysEqual(s.folder_path || [], currentFolders.surveys)).map((survey) => (
                <div key={survey._id} className="card survey-card">
                  <div className="card-header">
                    <h3>{survey.title}</h3>
                  </div>

                  {survey.description && (
                    <p className="card-description">{survey.description}</p>
                  )}

                  <div className="survey-meta">
                    <span>{survey.questions?.length || 0} questions</span>
                    {survey.updated_at && (
                      <>
                        <span>•</span>
                        <span>Updated {new Date(survey.updated_at).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn-action btn-edit"
                      onClick={() => navigate(`/synthetic-audience-workshop/surveys/${survey._id}`)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleDeleteSurvey(survey._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
              </>
            )}
          </div>
        </div>

        {/* Reports Section */}
        <div className="workspace-section">
          <div className="section-header">
            <div className="section-title-area">
              <h2>Reports</h2>
              {currentFolders.reports.length > 0 && (
                <div className="breadcrumb">
                  <button onClick={() => navigateToFolder('reports', [])} className="breadcrumb-link">
                    Root
                  </button>
                  {currentFolders.reports.map((segment, idx) => (
                    <React.Fragment key={idx}>
                      <span className="breadcrumb-separator">›</span>
                      <button
                        onClick={() => navigateToFolder('reports', currentFolders.reports.slice(0, idx + 1))}
                        className="breadcrumb-link"
                      >
                        {segment}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            <div className="section-header-buttons">
              <button
                className="btn-secondary"
                onClick={() => setNewFolderModal({ open: true, section: 'reports', name: '', access: ['All'] })}
              >
                + New Folder
              </button>
              <button
                className="btn-primary"
                onClick={() => setNewReportModalOpen(true)}
                disabled={totalCounts.surveys === 0 || totalCounts.personas === 0}
                title={totalCounts.surveys === 0 ? 'Create a survey first' : totalCounts.personas === 0 ? 'Create personas first' : ''}
              >
                + New Report
              </button>
            </div>
          </div>

          <div className="cards-container">
            {loading.reports ? (
              <div className="section-loading">
                <div className="section-loading-spinner"></div>
                <div className="section-loading-text">Loading reports...</div>
              </div>
            ) : (
              <>
                {/* Render folders first */}
                {folders.reports.map(folder => (
                  <div
                    key={folder._id}
                    className="card folder-card"
                    onClick={() => navigateToFolder('reports', [...currentFolders.reports, folder.name])}
                  >
                    <div className="folder-card-content">
                      <img src={folderIcon} alt="Folder" className="folder-icon" />
                      <h3>{folder.name}</h3>
                    </div>
                    <button
                      className="folder-access-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFolderAccessModal(folder);
                      }}
                      title="Edit folder access"
                    >
                      👥
                    </button>
                    <button
                      className="folder-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder._id);
                      }}
                      title="Delete folder"
                    >
                      ×
                    </button>
                  </div>
                ))}

                {/* Then render items at current level */}
                {sortedReports.filter(r => arraysEqual(r.folder_path || [], currentFolders.reports)).length === 0 && folders.reports.length === 0 ? (
              <div className="empty-state">
                <p>No reports in this folder yet</p>
                <p className="empty-subtitle">Create a report to run a survey against personas</p>
              </div>
            ) : (
              sortedReports.filter(r => arraysEqual(r.folder_path || [], currentFolders.reports)).map((report) => {
                const progressPercent = report.progress?.total_responses_needed > 0
                  ? Math.round((report.progress.completed_responses / report.progress.total_responses_needed) * 100)
                  : 0;

                return (
                  <div key={report._id} className="card report-card">
                    <div className="card-header">
                      <h3>{report.title}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(report.status)}`}>
                        {report.status}
                      </span>
                    </div>

                    <div className="report-meta">
                      <div className="meta-survey">Survey: {report.survey_snapshot?.title || 'Unknown'}</div>
                      <div className="meta-details">
                        <span>{report.selected_personas?.length || 0} personas</span>
                        <span className="meta-separator">•</span>
                        <span>{report.survey_snapshot?.questions?.length || 0} questions</span>
                        {report.created_at && (
                          <>
                            <span className="meta-separator">•</span>
                            <span>{new Date(report.created_at).toLocaleDateString()}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {report.status === 'running' && (
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <span className="progress-text">{progressPercent}% complete</span>
                      </div>
                    )}

                    <div className="card-actions">
                      <button
                        className={`btn-action ${report.status === 'running' ? 'btn-progress' : 'btn-results'}`}
                        onClick={() => navigate(`/synthetic-audience-workshop/reports/${report._id}/results`)}
                      >
                        {report.status === 'running' ? 'View Progress' : 'View Results'}
                      </button>
                      <button
                        className="btn-action btn-share"
                        onClick={() => openReportAccessModal(report)}
                      >
                        Share
                      </button>
                      <button
                        className="btn-action btn-delete"
                        onClick={() => handleDeleteReport(report._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
              </>
            )}
          </div>
        </div>
      </div>

      {newReportModalOpen && (
        <NewReportModal
          isOpen={newReportModalOpen}
          onClose={() => setNewReportModalOpen(false)}
          surveys={surveys}
          personas={personas}
          reports={reports}
          reportsFolders={folders.reports}
          defaultReportsFolder={currentFolders.reports}
          onReportCreated={handleReportCreated}
        />
      )}

      {/* New Folder Modal */}
      {newFolderModal.open && (
        <div className="modal-overlay" onClick={resetNewFolderModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Folder</h2>
              <button className="modal-close" onClick={resetNewFolderModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Folder Name</label>
                <input
                  type="text"
                  value={newFolderModal.name}
                  placeholder="Enter folder name"
                  maxLength={50}
                  autoFocus
                  onChange={(e) => setNewFolderModal(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateFolder(newFolderModal.section, newFolderModal.name, newFolderModal.access);
                    }
                  }}
                  className="form-input"
                />
                <small className="form-hint">Max 50 characters, no slashes</small>
              </div>
              <div className="form-group">
                <label>Folder Access</label>
                <AccessControl
                  value={newFolderModal.access}
                  onChange={(accessList) => setNewFolderModal(prev => ({ ...prev, access: accessList }))}
                />
                <small className="form-hint">Users must have access to the folder to see its contents in navigation</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={resetNewFolderModal}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => handleCreateFolder(newFolderModal.section, newFolderModal.name, newFolderModal.access)}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {folderAccessModal.open && (
        <div className="modal-overlay" onClick={closeFolderAccessModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Folder Access</h2>
              <button className="modal-close" onClick={closeFolderAccessModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Folder</label>
                <input type="text" value={folderAccessModal.folderName} className="form-input" disabled />
              </div>
              <div className="form-group">
                <label>Access</label>
                <AccessControl
                  value={folderAccessModal.access}
                  onChange={(accessList) => setFolderAccessModal(prev => ({ ...prev, access: accessList }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeFolderAccessModal}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleUpdateFolderAccess}>
                Save Access
              </button>
            </div>
          </div>
        </div>
      )}

      {reportAccessModal.open && (
        <div className="modal-overlay" onClick={closeReportAccessModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Report Access</h2>
              <button className="modal-close" onClick={closeReportAccessModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Report</label>
                <input type="text" value={reportAccessModal.reportTitle} className="form-input" disabled />
              </div>
              <div className="form-group">
                <label>Access</label>
                <AccessControl
                  value={reportAccessModal.access}
                  onChange={(accessList) => setReportAccessModal(prev => ({ ...prev, access: accessList }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeReportAccessModal}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleUpdateReportAccess}>
                Save Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default SyntheticAudienceWorkspace;
